/**
 * Modal de edición/creación de profesional.
 * Extraído de StaffPage para mantener el archivo principal por debajo del
 * límite de líneas del proyecto. La pivot de servicios (post-0028) vive aquí
 * vía StaffServicesSection (vista inversa de service_staff).
 */
import { useEffect, useRef, useState, useMemo } from 'react';
import { adminStaffApi, adminStaffPhotoApi, adminCentersApi } from '@api/client';
import type { Service, Staff } from '@api/types';
import * as Icons from '@components/Icons';
import { StringListEditor } from '@components/admin/StringListEditor';
import { StaffServicesSection } from './StaffServicesSection';

export interface CenterOption {
  id: string;
  name: string;
}

interface StaffModalProps {
  staff: Staff | null;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
  /** Superadmin only: enables the multi-centre selectors. */
  isSuper: boolean;
  centers: readonly CenterOption[];
  /** Edit flow (superadmin) — immediate sub-resource mutations. */
  onAddCenter?: (centerId: string) => Promise<unknown>;
  onRemoveCenter?: (centerId: string) => Promise<unknown>;
  /** Edit flow (superadmin) — catálogo global de servicios para la pivot inversa. */
  allServices?: readonly Service[];
}

export function StaffModal({ staff, onClose, onSave, isSaving, isSuper, centers, onAddCenter, onRemoveCenter, allServices = [] }: StaffModalProps) {
  const [name, setName] = useState(staff?.name ?? '');
  const [email, setEmail] = useState(staff?.email ?? '');
  const [role, setRole] = useState<'staff' | 'center_admin'>(staff?.role ?? 'staff');
  const [active, setActive] = useState(staff?.active ?? true);
  const [sortOrder, setSortOrder] = useState<number>(staff?.sortOrder ?? 0);
  const [err, setErr] = useState('');

  // Tabs internos (solo en edición). En creación queda plano.
  const [innerTab, setInnerTab] = useState<'perfil' | 'centros'>('perfil');

  // Perfil personal — todos editables, se persisten via PUT
  // /api/admin/staff/:id/profile. Sincroniza users.* + staff.name (si
  // hay user linkado) para que Mi perfil y este modal usen el mismo dato.
  const [lastName, setLastName] = useState(staff?.lastName ?? '');
  const [phone, setPhone] = useState(staff?.phone ?? '');
  const [jobTitle, setJobTitle] = useState(staff?.jobTitle ?? '');
  const [bio, setBio] = useState(staff?.bio ?? '');
  const [credentials, setCredentials] = useState<string[]>(staff?.credentials ?? []);
  const [functions, setFunctions] = useState<string[]>(staff?.functions ?? []);
  const [websiteUrl, setWebsiteUrl] = useState<string>(staff?.websiteUrl ?? '');
  // Toggle "perfil público" — solo superadmin lo ve y lo persiste.
  // En la UI lo invertimos: el switch ON significa "perfil no público".
  // Default: visible (publicProfileEnabled=true → switch OFF).
  const [publicProfileEnabled, setPublicProfileEnabled] = useState<boolean>(
    staff?.publicProfileEnabled ?? true,
  );
  // Foto: estado local opcional para mostrar la última URL tras subir.
  // Si null, usa staff.photoUrl.
  const [photoUrlLocal, setPhotoUrlLocal] = useState<string | null | undefined>(undefined);
  const [photoErr, setPhotoErr] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const photoUrl = photoUrlLocal !== undefined ? photoUrlLocal : staff?.photoUrl ?? null;

  // Create flow (superadmin): which centres the new staff belongs to.
  const [selectedCenterIds, setSelectedCenterIds] = useState<string[]>([]);
  // Edit flow (superadmin): live list of assigned centres, plus its own error line.
  const [centerIds, setCenterIds] = useState<string[]>(staff?.centerIds ?? []);
  const [centerErr, setCenterErr] = useState('');
  const [centerBusy, setCenterBusy] = useState(false);

  // Edit flow (superadmin): servicios del catálogo global que este staff ofrece.
  // La pivot service_staff es la misma que se edita desde ServiceModal — esta
  // es la vista inversa.
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [servicesDirty, setServicesDirty] = useState(false);

  // Edit flow (superadmin): centros donde este staff (su user linkado) es
  // admin. Migración 0029 / pivote user_centers. Cambios se aplican
  // inmediatamente vía grant/revoke; no se diferiere al onSave.
  const [adminCenterIds, setAdminCenterIds] = useState<Set<string>>(
    new Set(staff?.adminCenterIds ?? []),
  );
  const [adminGrantBusy, setAdminGrantBusy] = useState<string | null>(null);
  const [adminGrantErr, setAdminGrantErr] = useState('');
  const linkedUserId = staff?.userId ?? null;

  const isEdit = staff !== null;
  const centerNameById = useMemo(() => new Map(centers.map((c) => [c.id, c.name])), [centers]);
  const unassignedCenters = useMemo(
    () => centers.filter((c) => !centerIds.includes(c.id)),
    [centers, centerIds],
  );

  function toggleSelectedCenter(id: string) {
    setSelectedCenterIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }

  async function handleAddCenter(id: string) {
    if (!onAddCenter || !id) return;
    setCenterErr('');
    setCenterBusy(true);
    try {
      await onAddCenter(id);
      setCenterIds((prev) => [...prev, id]);
    } catch {
      setCenterErr('No se pudo añadir el centro.');
    } finally {
      setCenterBusy(false);
    }
  }

  async function handleRemoveCenter(id: string) {
    if (!onRemoveCenter) return;
    setCenterErr('');
    setCenterBusy(true);
    try {
      await onRemoveCenter(id);
      setCenterIds((prev) => prev.filter((c) => c !== id));
    } catch {
      setCenterErr('No se pudo quitar el centro.');
    } finally {
      setCenterBusy(false);
    }
  }

  // Cargar los servicios que actualmente ofrece este staff (vista inversa de
  // la pivot service_staff). Solo en flujo edit + superadmin.
  useEffect(() => {
    if (!isEdit || !isSuper) {
      setServicesLoaded(true);
      return;
    }
    let cancelled = false;
    void adminStaffApi.getServices(staff.id).then((resp) => {
      if (cancelled) return;
      setSelectedServiceIds(new Set(resp.serviceIds));
      setServicesLoaded(true);
    }).catch(() => {
      if (!cancelled) setServicesLoaded(true);
    });
    return () => { cancelled = true; };
  }, [isEdit, isSuper, staff]);

  function toggleService(serviceId: string) {
    setServicesDirty(true);
    setSelectedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  }

  async function toggleAdminGrant(centerId: string) {
    if (!linkedUserId) return;
    setAdminGrantErr('');
    setAdminGrantBusy(centerId);
    try {
      const isAdmin = adminCenterIds.has(centerId);
      if (isAdmin) {
        await adminCentersApi.revokeAdmin(centerId, linkedUserId);
        setAdminCenterIds((prev) => {
          const next = new Set(prev);
          next.delete(centerId);
          return next;
        });
      } else {
        await adminCentersApi.grantAdmin(centerId, linkedUserId);
        setAdminCenterIds((prev) => new Set(prev).add(centerId));
      }
    } catch {
      setAdminGrantErr('No se pudo actualizar el permiso. Reintenta.');
    } finally {
      setAdminGrantBusy(null);
    }
  }

  async function handlePickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    if (!isEdit || !linkedUserId) return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoErr('');
    if (file.size > 2 * 1024 * 1024) {
      setPhotoErr('La foto debe pesar menos de 2 MB.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoErr('Usa JPG, PNG o WebP.');
      return;
    }
    setPhotoBusy(true);
    try {
      const { photoUrl: nextUrl } = await adminStaffPhotoApi.upload(staff.id, file);
      setPhotoUrlLocal(nextUrl);
    } catch {
      setPhotoErr('No se pudo subir la foto.');
    } finally {
      setPhotoBusy(false);
    }
  }

  async function handleDeletePhoto() {
    if (!isEdit) return;
    setPhotoErr('');
    setPhotoBusy(true);
    try {
      await adminStaffPhotoApi.delete(staff.id);
      setPhotoUrlLocal(null);
    } catch {
      setPhotoErr('No se pudo eliminar la foto.');
    } finally {
      setPhotoBusy(false);
    }
  }

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (!isEdit && isSuper && selectedCenterIds.length === 0) {
      setErr('Selecciona al menos un centro.');
      return;
    }
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      setErr('El orden debe ser un entero ≥ 0.');
      return;
    }
    try {
      if (isEdit) {
        // 1. Perfil — name/lastName/phone/jobTitle/bio van por el endpoint
        //    /profile que sincroniza users.* y staff.name. Único nombre,
        //    coherente con Mi perfil.
        const credentialsClean = credentials.map((l) => l.trim()).filter((l) => l.length > 0);
        const functionsClean = functions.map((l) => l.trim()).filter((l) => l.length > 0);
        await adminStaffApi.updateProfile(staff.id, {
          name: name.trim(),
          lastName: lastName.trim() === '' ? null : lastName.trim(),
          phone: phone.trim() === '' ? null : phone.trim(),
          jobTitle: jobTitle.trim() === '' ? null : jobTitle.trim(),
          bio: bio.trim() === '' ? null : bio.trim(),
          credentials: credentialsClean.length === 0 ? null : credentialsClean,
          functions: functionsClean.length === 0 ? null : functionsClean,
          websiteUrl: websiteUrl.trim() === '' ? null : websiteUrl.trim(),
          ...(isSuper && linkedUserId ? { publicProfileEnabled } : {}),
        });
        // 2. Operativa — active + sortOrder via updateStaff legacy. NO
        //    incluimos `name` aquí: lo gestiona el endpoint /profile.
        await onSave({ email: email.trim() || undefined, active, sortOrder });
        // 3. Persist services pivot if dirty (solo superadmin lo edita).
        if (isSuper && servicesDirty) {
          await adminStaffApi.setServices(staff.id, Array.from(selectedServiceIds));
          setServicesDirty(false);
        }
      } else {
        await onSave({
          name: name.trim(),
          email: email.trim(),
          role,
          active,
          ...(isSuper ? { centerIds: selectedCenterIds } : {}),
        });
      }
    } catch {
      setErr('Error al guardar. Inténtalo de nuevo.');
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">{isEdit ? `Editar profesional · ${staff.name}` : 'Invitar profesional'}</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {err && (
            <div style={{ padding: '10px 14px', background: 'var(--danger-soft)', border: '1px solid oklch(0.85 0.05 25)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--accent-dark)', marginBottom: 12 }}>
              {err}
            </div>
          )}

          {/* ─── CREATE FLOW (flat, sin tabs) ─────────────────────────── */}
          {!isEdit && (
            <>
              <div className="field">
                <label className="field-label">Nombre <span style={{ color: 'var(--accent)' }}>*</span></label>
                <input className="input" value={name} onChange={(e) => { setName(e.target.value); }} autoFocus />
              </div>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Email<span style={{ color: 'var(--accent)' }}> *</span></label>
                  <input className="input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); }} />
                </div>
                <div className="field">
                  <label className="field-label">Rol</label>
                  <select className="select" value={role} onChange={(e) => { setRole(e.target.value as 'staff' | 'center_admin'); }}>
                    <option value="staff">Staff</option>
                    <option value="center_admin">Administrador</option>
                  </select>
                </div>
              </div>
              {email.trim() && (
                <p style={{ fontSize: 12, color: 'var(--fg-soft)', marginTop: -4, marginBottom: 4 }}>
                  Le enviaremos un email con un enlace de acceso. No necesita contraseña.
                </p>
              )}
              {isSuper && (
                <div className="field">
                  <label className="field-label">Centros <span style={{ color: 'var(--accent)' }}>*</span></label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 8 }}>
                    {centers.length === 0 && <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>Sin centros disponibles.</span>}
                    {centers.map((c) => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedCenterIds.includes(c.id)}
                          onChange={() => { toggleSelectedCenter(c.id); }}
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                  <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
                    El profesional podrá tener reservas y horarios en cada centro seleccionado.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ─── EDIT FLOW (con tabs internos Perfil / Centros) ────────── */}
          {isEdit && (
            <>
              <div className="seg" style={{ marginBottom: 16 }}>
                <button
                  type="button"
                  className="seg-btn"
                  data-active={innerTab === 'perfil'}
                  onClick={() => { setInnerTab('perfil'); }}
                >
                  Perfil
                </button>
                <button
                  type="button"
                  className="seg-btn"
                  data-active={innerTab === 'centros'}
                  onClick={() => { setInnerTab('centros'); }}
                >
                  Centros
                </button>
              </div>
            </>
          )}

          {isEdit && innerTab === 'perfil' && (
            <>
              {linkedUserId ? (
                <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: photoUrl
                        ? `center/cover no-repeat url(${photoUrl})`
                        : 'var(--surface-2, #f1f5f4)',
                      border: '1px solid var(--border, #e5e7eb)',
                      flexShrink: 0,
                    }}
                    aria-label={photoUrl ? 'Foto de perfil' : 'Sin foto'}
                  />
                  <div style={{ display: 'grid', gap: 6 }}>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => { void handlePickPhoto(e); }}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn"
                        disabled={photoBusy}
                        onClick={() => fileRef.current?.click()}
                      >
                        {photoBusy ? 'Subiendo…' : photoUrl ? 'Cambiar foto' : 'Subir foto'}
                      </button>
                      {photoUrl && (
                        <button
                          type="button"
                          className="btn"
                          disabled={photoBusy}
                          onClick={() => { void handleDeletePhoto(); }}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                    <span style={{ color: 'var(--fg-soft)', fontSize: 12 }}>
                      JPG, PNG o WebP. Máx 2 MB.
                    </span>
                    {photoErr && (
                      <span style={{ color: 'var(--danger)', fontSize: 12 }}>{photoErr}</span>
                    )}
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 12, color: 'var(--fg-soft)', marginBottom: 8 }}>
                  La foto y las credenciales estarán disponibles cuando este profesional
                  acepte la invitación y tenga sesión.
                </p>
              )}
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Nombre <span style={{ color: 'var(--accent)' }}>*</span></label>
                  <input className="input" value={name} onChange={(e) => { setName(e.target.value); }} autoFocus />
                </div>
                <div className="field">
                  <label className="field-label">Apellidos</label>
                  <input
                    className="input"
                    maxLength={200}
                    value={lastName}
                    onChange={(e) => { setLastName(e.target.value); }}
                  />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Email</label>
                <input className="input" type="email" value={email} readOnly />
                <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
                  El email es inmutable — define la cuenta de acceso.
                </p>
              </div>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Teléfono</label>
                  <input
                    className="input"
                    type="tel"
                    maxLength={200}
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); }}
                    placeholder="+34 612 345 678"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Cargo / rol mostrado</label>
                  <input
                    className="input"
                    maxLength={200}
                    value={jobTitle}
                    onChange={(e) => { setJobTitle(e.target.value); }}
                    placeholder="Ej.: Psicóloga"
                  />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Bio</label>
                <textarea
                  className="input"
                  rows={3}
                  maxLength={200}
                  value={bio}
                  onChange={(e) => { setBio(e.target.value); }}
                  placeholder="Breve descripción profesional (máx. 200 caracteres)"
                />
              </div>
              {linkedUserId && (
                <>
                  <div className="field">
                    <label className="field-label">Credenciales / títulos</label>
                    <StringListEditor
                      value={credentials}
                      onChange={setCredentials}
                      singular="credencial"
                      placeholder="Ej.: Grado en Nutrición Humana y Dietética"
                      emptyLabel="Sin credenciales aún."
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">Funciones (chips de marketing)</label>
                    <StringListEditor
                      value={functions}
                      onChange={setFunctions}
                      singular="función"
                      placeholder="Ej.: Consulta Dietética, Dirección, Coordinación…"
                      emptyLabel="Sin funciones aún."
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">Página web personal</label>
                    <input
                      className="input"
                      type="url"
                      maxLength={1024}
                      value={websiteUrl}
                      onChange={(e) => { setWebsiteUrl(e.target.value); }}
                      placeholder="https://…"
                    />
                  </div>
                </>
              )}
              <div className="switch-row">
                <div className="switch-row-info">
                  <div className="switch-row-name">Profesional activo</div>
                  <div className="switch-row-desc">Los profesionales inactivos no aparecen en el widget.</div>
                </div>
                <div className="switch" data-on={active} onClick={() => { setActive((v) => !v); }} />
              </div>
              {isSuper && linkedUserId && (
                <div className="switch-row">
                  <div className="switch-row-info">
                    <div className="switch-row-name">Perfil no público</div>
                    <div className="switch-row-desc">
                      Si está activado, este profesional NO aparece en la web pública
                      (equipomag.com). El widget de reservas no se ve afectado.
                    </div>
                  </div>
                  <div
                    className="switch"
                    data-on={!publicProfileEnabled}
                    onClick={() => { setPublicProfileEnabled((v) => !v); }}
                  />
                </div>
              )}
            </>
          )}

          {isEdit && innerTab === 'centros' && (
            <>
              <div className="field">
                <label className="field-label">Orden de asignación</label>
                <input
                  className="input mono"
                  type="number"
                  min={0}
                  step={1}
                  value={sortOrder}
                  onChange={(e) => { setSortOrder(parseInt(e.target.value, 10) || 0); }}
                  style={{ width: 120 }}
                />
                <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
                  Menor primero. Define el orden de asignación cuando el cliente reserva con "Cualquier profesional".
                </p>
              </div>
              {isSuper && (
                <div className="field">
                  <label className="field-label">Centros</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {centerIds.length === 0 && <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>Sin centros asignados.</span>}
                    {centerIds.map((cid) => (
                      <span key={cid} className="pill" data-tone="neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {centerNameById.get(cid) ?? cid}
                        <button
                          type="button"
                          className="icon-btn"
                          data-variant="ghost"
                          disabled={centerBusy}
                          onClick={() => { void handleRemoveCenter(cid); }}
                          aria-label={`Quitar ${centerNameById.get(cid) ?? cid}`}
                          style={{ padding: 0, width: 16, height: 16 }}
                        >
                          <Icons.X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <select
                    className="select"
                    value=""
                    disabled={centerBusy || unassignedCenters.length === 0}
                    onChange={(e) => { if (e.target.value) void handleAddCenter(e.target.value); }}
                  >
                    <option value="">{unassignedCenters.length === 0 ? 'Asignado a todos los centros' : 'Añadir centro…'}</option>
                    {unassignedCenters.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {centerErr && (
                    <p style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>{centerErr}</p>
                  )}
                </div>
              )}
              {isSuper && linkedUserId && (
                <div className="field">
                  <label className="field-label">Administra centros</label>
                  <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: -2, marginBottom: 8 }}>
                    Centros donde este profesional puede entrar como administrador (login + gestión).
                    Cambios se aplican al instante; tendrá que volver a iniciar sesión para verlos en su menú.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 8 }}>
                    {centers.length === 0 && <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>Sin centros disponibles.</span>}
                    {centers.map((c) => {
                      const checked = adminCenterIds.has(c.id);
                      const busy = adminGrantBusy === c.id;
                      return (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={busy}
                            onChange={() => { void toggleAdminGrant(c.id); }}
                            style={{ accentColor: 'var(--accent)' }}
                          />
                          {c.name}
                        </label>
                      );
                    })}
                  </div>
                  {adminGrantErr && (
                    <p style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>{adminGrantErr}</p>
                  )}
                </div>
              )}
              {isSuper && (
                <StaffServicesSection
                  allServices={allServices}
                  selectedIds={selectedServiceIds}
                  loaded={servicesLoaded}
                  onToggle={toggleService}
                />
              )}
            </>
          )}
        </form>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn" data-variant="primary" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Invitar'}
          </button>
        </div>
      </div>
    </div>
  );
}

